<script setup>
/**
 * ProfileEntriesPanel — the My Notes tab on a restaurant's page: what the
 * member has told RM about ONE restaurant, newest first.
 *
 * Kapil, 2026-09-22:
 *  - Her note comes first, ONCE, and says what it is: "Private Only", or
 *    "Highlight shared with your Network". The shared wording itself lives on the
 *    Member Highlights tab next door, so it is not repeated here.
 *  - "+ Add/Update notes" sits BELOW the last note and opens the Note pop-up
 *    (RestaurantNotesDialog, the same one Favorites opens). The empty box
 *    shows only when there is nothing here yet.
 *  - No "From your Network" list: that is what Member Highlights is for.
 *
 * Private by default; a share is a separate, explicitly approved wording.
 * A saved contact is not a note: it can never be shared.
 *
 * Kapil, 2026-09-23: every entry is edited IN PLACE here — no trip to the
 * pop-up (ProfileNoteItem). One note, one text (2026-09-24): a note that is
 * shared IS the Highlight, so each note has one Edit and one status, shown
 * under its time.
 */
import { computed, onMounted, ref, watch } from 'vue'
import axios from '@axios'
import { storeToRefs } from 'pinia'
import { relativeAgo } from '@/utils/relativeAgo'
import ProfileShareDecision from '@core/components/ProfileShareDecision.vue'
import RestaurantNotesDialog from '@core/components/RestaurantNotesDialog.vue'
import { NOTE_MAX_LENGTH } from '@/utils/profileNotes'
import { useRestaurantsStore, useUsersStore } from '@/stores'
import { mediaUrl } from '@core/utils/mediaUrl'
import ProfileNoteItem from '@core/components/ProfileNoteItem.vue'
import { activeShares, entryAt, onNetwork, removeQuestion, restaurantShareOpen, statusLine } from '@/utils/noteDisplay'

const props = defineProps({
  restaurantId: { type: String, required: true },
  restaurantName: { type: String, default: '' },
})

// A share or a Make private changes what the Member Highlights tab next door
// should show, so the page reloads it (and shows the tab the first time).
const emit = defineEmits(['changed'])

const restaurantsStore = useRestaurantsStore()
const usersStore = useUsersStore()
const { myUser } = storeToRefs(usersStore)

const isMember = computed(() => !!myUser.value?._id && myUser.value.userLevel !== 'dummy')

const loading = ref(false)
const entries = ref([])
const error = ref('')

// The first note, typed straight into the box when there is nothing here yet.
const composer = ref('')
const saving = ref(false)

// After that first save, the member decides who it is for — the same decision
// RM AI offers, reachable without RM AI, which is Platinum-only.
const shareDecision = ref(null)

// Sharing a note that was kept private until now.
const sharingEntryId = ref(null)

const notesDialogOpen = ref(false)

// The one entry being edited in place, if any.
const editingId = ref(null)

function fmtDate(d) {
  if (!d) return ''

  return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

// RM's shared relative timestamps for normal recency — "28m ago", "3d ago"
// (Kapil, 2026-09-21). fmtDate stays for a visit DATE, which is a real date.
function ago(d) {
  return d ? `${relativeAgo(d)} ago` : ''
}

function kindLabel(e) {
  if (e.kind === 'contact') return 'Contact'
  if (e.kind === 'experience') return e.visitDate ? `Visit · ${fmtDate(e.visitDate)}` : 'Visit'
  if (e.kind === 'preference') return 'Preference'

  return ''
}

/**
 * The business card the member photographed, kept with the person it created
 * — the same way a receipt stays with its booking (Kapil, 2026-09-22). The id
 * has been stored on `source.imageId` since capture; nothing showed it.
 */
// A thumbnail that fails to load is dropped by ProfileContactLine itself, so
// every surface that shows a saved card behaves the same way.
function cardImageId(e) {
  return (e && e.source && e.source.imageId) || null
}

// Opens in place rather than a new browser tab (Kapil, 2026-09-22): tapping a
// card on a phone should not leave the app.
const cardDialogImageId = ref(null)

function openCard(imageId) {
  if (imageId) cardDialogImageId.value = imageId
}

// Kapil's words (2026-09-22) — decided once, in utils/noteDisplay. One note,
// one text, one status (2026-09-24).
const status = e => statusLine(e, props.restaurantName)

async function load() {
  if (!isMember.value || !props.restaurantId) return
  loading.value = true
  error.value = ''
  try {
    const { data } = await axios.get('/profile/entries', { params: { restaurantId: props.restaurantId } })

    entries.value = data || []
  } catch {
    // entries are deliberately NOT cleared — a failed refresh should leave
    // what the member was reading on screen.
    error.value = 'Could not load your notes right now.'
  } finally {
    loading.value = false
  }
}

async function add() {
  const text = composer.value.trim()
  if (!text || saving.value) return
  saving.value = true
  try {
    const { data } = await axios.post('/profile/entries', { text, restaurantId: props.restaurantId })

    composer.value = ''
    await load()
    restaurantsStore.refreshFavoriteNotes()

    // Their exact words, for them to edit if they want. RM does not guess at
    // what is private in a typed note — it cannot detect a friend's name
    // server-side, and guessing would be worse than not trying.
    const saved = entries.value.find(e => e.entryId === data?.entryId) || null

    shareDecision.value = data?.entryId
      ? { entryId: data.entryId, canShareWithRestaurant: restaurantShareOpen(saved) }
      : null
  } catch (e) {
    error.value = e?.response?.data?.message || 'Could not save right now.'
  } finally {
    saving.value = false
  }
}

// "Make private" stops every audience seeing it at once: the member sees one
// status, so one action undoes it. Their note is
// untouched.
async function makePrivate(e) {
  try {
    for (const sh of activeShares(e)) {
      // eslint-disable-next-line no-await-in-loop
      await axios.delete(`/profile/shares/${sh.shareId}`)
    }
    await reloadAndNotify()
  } catch (err) {
    error.value = err?.response?.data?.message || 'Could not make that private right now.'
  }
}

function startEdit(e) {
  editingId.value = e.entryId
}

function doneEditing() {
  editingId.value = null
}

// Always asked, in the pop-up's words: a shared note takes its share with it,
// so the Member Highlights tab next door reloads too.
async function remove(e) {
  // eslint-disable-next-line no-alert
  if (!window.confirm(removeQuestion(e))) return
  try {
    await axios.delete(`/profile/entries/${e.entryId}`)
    await reloadAndNotify()
  } catch (err) {
    error.value = err?.response?.data?.message || 'Could not remove right now.'
  }
}

/**
 * After ANY change here — a share, Make private, an edit, a removal — the
 * member's Favorites row has to show it too. Only adding a note refreshed the
 * shared store, so an edit made on the restaurant page left the Favorites row
 * showing the old note until a reload (Kapil, 2026-09-23).
 */
async function reloadAndNotify() {
  await load()
  restaurantsStore.refreshFavoriteNotes()
  emit('changed')
}

onMounted(load)
watch(() => props.restaurantId, load)
</script>

<template>
  <div
    v-if="isMember"
    class="profile-entries"
  >
    <!--
      Nothing here yet: the box IS the first thing, as it always was.
      NOT when the load failed, though (Kapil, 2026-09-22): a failed fetch
      leaves the list empty, and an empty composer under "Private to you. Only
      you see this." reads as though the member's notes are gone.
    -->
    <template v-if="!entries.length && !loading && !shareDecision && !error">
      <VTextarea
        v-model="composer"
        class="profile-entries__input"
        placeholder="Favorite dishes, tables, team members, occasions, who recommended..."
        :counter="NOTE_MAX_LENGTH"
        :maxlength="NOTE_MAX_LENGTH"
        rows="3"
        auto-grow
        variant="outlined"
        density="compact"
        hide-details="auto"
      />
      <div
        v-if="composer.trim()"
        class="d-flex justify-end gap-2 mt-2"
      >
        <VBtn
          variant="text"
          size="small"
          @click="composer = ''"
        >
          Cancel
        </VBtn>
        <VBtn
          color="primary"
          variant="flat"
          size="small"
          rounded="lg"
          :loading="saving"
          @click="add"
        >
          Save Note
        </VBtn>
      </div>
      <p class="profile-entries__privacy">
        Private to you. Only you see this.
      </p>
    </template>

    <ProfileShareDecision
      v-if="shareDecision"
      :entry-id="shareDecision.entryId"
      :restaurant-name="restaurantName"
      :can-share-with-restaurant="shareDecision.canShareWithRestaurant"
      @done="shareDecision = null"
      @shared="reloadAndNotify"
    />

    <p
      v-if="error"
      class="profile-entries__error"
    >
      {{ error }}
      <button
        type="button"
        class="profile-entries__link"
        @click="load"
      >
        Try again
      </button>
    </p>

    <div
      v-if="loading && !entries.length"
      class="profile-entries__skeleton"
    >
      <div class="profile-entries__skeleton-line" />
      <div class="profile-entries__skeleton-line short" />
    </div>

    <!-- Her notes (primary): the words first, then what they are. -->
    <ul
      v-if="entries.length"
      class="profile-entries__list"
    >
      <li
        v-for="e in entries"
        :key="e.entryId"
        class="profile-entries__row"
      >
        <!--
          The note's body — their words, then the person and the card — drawn
          the same way on every surface, and edited in place (ProfileNoteItem).
        -->
        <ProfileNoteItem
          :entry="e"
          :restaurant-name="restaurantName"
          text-class="profile-entries__text"
          :editing="editingId === e.entryId"
          @open-card="openCard"
          @done="doneEditing"
          @changed="reloadAndNotify"
        >
          <!--
            Under the note: when, then what it is, then what they can do
            (Kapil, 2026-09-24: "Underneath the timestamp, it should show
            highlight shared with your network and NAIA. That's it.").
          -->
          <template #meta>
            <p class="profile-entries__time">
              {{ ago(entryAt(e)) }}<template v-if="kindLabel(e) && e.kind !== 'contact'">
                · {{ kindLabel(e) }}
              </template>
            </p>
            <p class="profile-entries__status">
              {{ status(e) }}
            </p>
            <div class="profile-entries__meta">
              <button
                v-if="editingId !== e.entryId"
                type="button"
                class="profile-entries__link"
                @click="startEdit(e)"
              >
                Edit
              </button>
              <button
                v-if="e.kind !== 'contact' && !onNetwork(e) && sharingEntryId !== e.entryId"
                type="button"
                class="profile-entries__link"
                @click="sharingEntryId = e.entryId"
              >
                Share
              </button>
              <button
                v-if="e.kind !== 'contact' && activeShares(e).length"
                type="button"
                class="profile-entries__link"
                @click="makePrivate(e)"
              >
                Make private
              </button>

              <!-- The pop-up's actions, in its order; Remove always asks first. -->
              <button
                type="button"
                class="profile-entries__link"
                @click="remove(e)"
              >
                Remove
              </button>
            </div>

            <ProfileShareDecision
              v-if="sharingEntryId === e.entryId"
              class="mt-2"
              :entry-id="e.entryId"
              :restaurant-name="restaurantName"
              :can-share-with-restaurant="restaurantShareOpen(e)"
              :just-saved="false"
              @done="sharingEntryId = null"
              @shared="reloadAndNotify"
            />
          </template>
        </ProfileNoteItem>
      </li>
    </ul>

    <!--
      BELOW the last note, never above it (Kapil, 2026-09-22): what they have
      told RM comes first. An ask sitting on top of their own words reads as
      though RM is always after more.
    -->
    <button
      v-if="entries.length"
      type="button"
      class="profile-entries__add"
      @click="notesDialogOpen = true"
    >
      + Add/Update notes
    </button>

    <!-- The saved card, full size, without leaving the page. -->
    <VDialog
      :model-value="!!cardDialogImageId"
      max-width="560"
      @update:model-value="cardDialogImageId = null"
    >
      <VCard class="profile-entries__card-dialog">
        <img
          v-if="cardDialogImageId"
          :src="mediaUrl(cardDialogImageId, 'original')"
          alt="Saved business card"
        >
        <div class="profile-entries__card-actions">
          <VBtn
            variant="text"
            size="small"
            @click="cardDialogImageId = null"
          >
            Close
          </VBtn>
        </div>
      </VCard>
    </VDialog>

    <RestaurantNotesDialog
      v-model="notesDialogOpen"
      :restaurant-id="restaurantId"
      :restaurant-name="restaurantName"
      @changed="reloadAndNotify"
    />
  </div>
</template>

<style scoped>
.profile-entries { padding: 0 0 16px; }
.profile-entries__input :deep(textarea) { font-size: 16px; }

.profile-entries__add {
  background: none;
  border: 0;
  color: rgb(var(--v-theme-primary));
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  padding: 8px 0;
}
.profile-entries__privacy { margin: 8px 0 0; font-size: 12px; color: var(--ds-text-muted, rgba(var(--v-theme-on-surface), 0.6)); }
.profile-entries__error { margin: 8px 0 0; font-size: 14px; color: rgb(var(--v-theme-error)); }
.profile-entries__list { list-style: none; margin: 8px 0 0; padding: 0; }
.profile-entries__row { padding: 12px 0; border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08); }
.profile-entries__row:last-child { border-bottom: 0; }
.profile-entries__text { font-size: 14px; line-height: 1.45; color: var(--ds-text-body, rgb(var(--v-theme-on-surface))); white-space: pre-wrap; }
.profile-entries__contact { margin-top: 4px; font-size: 14px; color: var(--ds-text-body, rgb(var(--v-theme-on-surface))); }
.profile-entries__card-dialog {
  padding: 12px;
  border-radius: 12px;
}

.profile-entries__card-dialog img {
  display: block;
  width: 100%;
  height: auto;
  border-radius: 8px;
}

.profile-entries__card-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 4px;
}

/* The saved card, on the receipt pattern: a contained thumbnail that opens
   the original. */
.profile-entries__card {
  display: block;
  margin-top: 8px;
  padding: 6px;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  max-width: 260px;
}

.profile-entries__card img {
  display: block;
  width: 100%;
  max-height: 150px;
  object-fit: contain;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.03);
}

.profile-entries__meta { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 12px; margin-top: 4px; font-size: 12px; color: var(--ds-text-muted, rgba(var(--v-theme-on-surface), 0.6)); }

/* What the note IS outranks when it was written. */
/* When, then what it is — each on its own line (Kapil, 2026-09-24). */
.profile-entries__time { margin: 4px 0 0; font-size: 12px; color: var(--ds-text-muted, rgba(var(--v-theme-on-surface), 0.6)); }
.profile-entries__status { margin: 2px 0 0; font-size: 12px; font-weight: 600; color: var(--ds-text-body, rgb(var(--v-theme-on-surface))); }
.profile-entries__link { background: none; border: 0; padding: 0; font: inherit; white-space: nowrap; color: rgb(var(--v-theme-primary)); cursor: pointer; }
.profile-entries__row-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px; }
.profile-entries__skeleton { margin-top: 16px; }
.profile-entries__skeleton-line { height: 14px; border-radius: 8px; background: rgba(var(--v-theme-on-surface), 0.08); margin-bottom: 10px; }
.profile-entries__skeleton-line.short { width: 60%; }
</style>
